import React, { useState } from 'react';
import { AlertTriangle, BarChart3, Flag, MapPin, Repeat2, Search, ShieldCheck, Truck } from 'lucide-react';
import TopBar from '../components/TopBar';
import PowerBIEmbed from '../components/PowerBIEmbed';
import StockoutMap from '../components/stockout-map/StockoutMap';
import '../modules/webv2/webV2Module.css';
import './StockPerformance.css';

const THRIVE_STOCK_REPORT_ID = 'c97f6da0-4f4a-4a53-9298-081f340be726';
const THRIVE_STOCK_WORKSPACE_ID = '7d441575-4aa9-4260-99dd-543f002c0340';
const THRIVE_STOCK_COUNTRY_FILTER = { table: 'z_Geo_admin', column: 'Country_Name' };

const thriveStockReportPages = [
  {
    key: 'visibility-reporting-quality',
    number: '1.',
    title: 'Visibility, Reporting Rates & Quality',
    pageName: '1. Visibility, Reporting Rates & Quality',
    description: 'Stock visibility, reporting quality, timeliness and country reporting gaps.',
    color: 'teal',
    icon: BarChart3
  },
  {
    key: 'stock-pipelines-risk-monitoring',
    number: '2.',
    title: 'Stock, Pipelines and Risk Monitoring',
    pageName: '2. Stock Coverage Time',
    description: 'Current stock, monthly consumption, pipeline, lead time and stock coverage signals.',
    color: 'blue',
    icon: Truck,
    children: [
      {
        key: 'stock-coverage-time',
        number: '2a.',
        title: 'Stock Coverage Trends',
        pageName: '2a. Country Stock Coverage Trends',
        icon: ShieldCheck
      },
      {
        key: 'stock-coverage-time-country',
        number: '2b.',
        title: 'Stock Coverage Time by Country',
        pageName: '2b. Stock, Pipelines and Risk Monitoring',
        icon: ShieldCheck
      }
    ]
  },
  {
    key: 'major-risk-highlights',
    number: '3.',
    title: 'Major Risk Highlights',
    pageName: '4. Major Stock Risk',
    description: 'Executive risk summaries by antigen and country with projected country risk.',
    color: 'purple',
    icon: Repeat2,
    children: [
      {
        key: 'country-risk-breakdown',
        number: '3a.',
        title: 'Country Risk Breakdown',
        pageName: '4a. Country Risks Breakdown',
        icon: Flag
      },
      {
        key: 'stock-coverage-projection',
        number: '3b.',
        title: 'Stock Coverage Projection',
        pageName: '4b. Stock Coverage Projection',
        icon: Flag
      }
    ]
  },
  {
    key: 'stockout-frequency-causes',
    number: '4.',
    title: 'Stockout Frequency & Causes',
    pageName: '5. Stockout Frequency & Causes',
    description: 'Stockout frequency, supply-chain burden and recurring cause analysis.',
    color: 'red',
    icon: Search,
    children: [
      {
        key: 'top-10-countries-stockouts',
        number: '4a.',
        title: 'Top 10 Countries with stockouts',
        pageName: '5a. Top 10 COs with Stockouts',
        icon: Search
      },
      {
        key: 'country-stock-status-causes',
        number: '4b.',
        title: 'Country Breakdown - Stock Status and Causes',
        pageName: '3a. Country breakdown - Stock Status and Causes',
        icon: Search
      }
    ]
  },
  {
    key: 'persistent-stockouts',
    number: '5.',
    title: 'Persistent Stockouts',
    pageName: '6. Persistent Stockouts Overview',
    description: 'Recurring stockouts and their duration, burden and service delivery impact.',
    color: 'green',
    icon: AlertTriangle,
    children: [
      {
        key: 'persistent-country-breakdown',
        number: '5a.',
        title: 'Country Breakdown',
        pageName: '6a. Persistent Stockouts Country Breakdown',
        icon: Repeat2
      }
    ]
  },
  {
    key: 'stockout-map',
    number: '',
    title: 'Stockout Map',
    description: 'Explore country stockouts by administrative area, antigen and reporting month.',
    color: 'teal',
    icon: MapPin
  }
];

const thriveStockReportPageMap = new Map(
  thriveStockReportPages.flatMap((page) => [
    [page.key, page],
    ...(page.children || []).map((child) => [child.key, { ...child, parentColor: page.color }])
  ])
);

const StockPerformance = ({ userClaims, isEmbedded = false }) => {
  const [activeStockReportPageKey, setActiveStockReportPageKey] = useState('visibility-reporting-quality');
  const activePage = thriveStockReportPageMap.get(activeStockReportPageKey) || thriveStockReportPages[0];

  const content = (
    <section className="home-section vaccines-page-section vaccines-dashboard thrive-stock-dashboard">
      <div className="vaccines-dashboard-header">
        <div>
          <h2 className="vaccines-dashboard-title">Vaccine Stock and Cold Chain Performance Dashboard</h2>
          <p className="vaccines-dashboard-subtitle">
            Create subnational visibility, monitor stock performance, track risks and outline chronic challenges.
          </p>
        </div>
      </div>

      <div className="thrive-stock-nav-card">
        <div className="thrive-stock-nav-header">
          <span className="vaccines-section-label">Report Pages</span>
          <strong>{activePage.number} {activePage.title}</strong>
          <p>{activePage.description || 'Focused Power BI view from the THRIVE360 stock performance report.'}</p>
        </div>
        <nav className="thrive-stock-menu-bar" aria-label="THRIVE360 stock performance pages">
          {thriveStockReportPages.map((page) => {
            const Icon = page.icon;
            const isParentActive = activePage.key === page.key || page.children?.some((child) => child.key === activePage.key);

            return (
              <div key={page.key} className="thrive-stock-menu-item">
                <button
                  type="button"
                  className={`thrive-stock-menu-button is-${page.color} ${isParentActive ? 'is-active' : ''}`}
                  onClick={() => setActiveStockReportPageKey(page.key)}
                >
                  <Icon size={16} aria-hidden="true" />
                  <span>{page.number} {page.title}</span>
                </button>
                {page.children?.length > 0 && (
                  <div className="thrive-stock-submenu" role="menu">
                    {page.children.map((child) => {
                      const ChildIcon = child.icon;

                      return (
                        <button
                          key={child.key}
                          type="button"
                          role="menuitem"
                          className={`thrive-stock-submenu-button ${activePage.key === child.key ? 'is-active' : ''}`}
                          onClick={() => setActiveStockReportPageKey(child.key)}
                        >
                          <ChildIcon size={15} aria-hidden="true" />
                          <span>{child.number} {child.title}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      {activePage.key === 'stockout-map' ? <StockoutMap /> : <div className="thrive-stock-report-card">
        <div className="global-coverage-embed thrive-stock-report-embed">
          <PowerBIEmbed
            reportId={THRIVE_STOCK_REPORT_ID}
            workspaceId={THRIVE_STOCK_WORKSPACE_ID}
            pageName={activePage.pageName}
            showFilterPane
            countryFilterTarget={THRIVE_STOCK_COUNTRY_FILTER}
            height="100%"
          />
        </div>
      </div>}
    </section>
  );

  if (isEmbedded) return content;

  return (
    <div className="min-h-screen bg-gray-100">
      <TopBar currentPage="stock-performance" userClaims={userClaims} />
      <div className="stock-performance-native-compat v2-module">
        {content}
      </div>
    </div>
  );
};

export default StockPerformance;
